import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
} from "@material-ui/core";
import { ExpandMore as ExpandMoreIcon } from "@material-ui/icons";
import PropTypes from "prop-types";
import { styled } from "@material-ui/styles";
import TextField from "./white-text-field.component";
import { isApprove, monthsToExpiryDate } from "../approval-wizard.constants";

const CustomSwitch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
    "&::before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    "&::after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
  },
}));

// Read-only applicant fields behind the accordion: 5 owner + 11 company. The
// pre-subscription plan block adds 3 more when the applicant picked a plan at
// sign-up. The two document buttons are actions, not fields, so they are not
// counted in the summary.
const APPLICANT_FIELD_COUNT = 16;
const PRE_SUBSCRIPTION_FIELD_COUNT = 3;

const sectionSx = { pt: 2, pb: 2 };

const BasicInfoStep = ({
  industries,
  selectedCompany,
  plans,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  setSelectedPlan,
  setActiveStep,
  isSubmitting,
  canEdit,
  rejectInputComponentProps,
  classes,
}) => {
  const hasPreSubscription = selectedCompany.owner.registrationType === 1;
  const readOnlyFieldCount =
    APPLICANT_FIELD_COUNT + (hasPreSubscription ? PRE_SUBSCRIPTION_FIELD_COUNT : 0);
  const showExpiryPreview =
    /^\d+$/.test(values.expireDate) && Number(values.expireDate) > 0;

  return (
    <>
      <Accordion className={classes.applicantAccordion}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography variant="subtitle2">Applicant details</Typography>
            <Typography variant="caption" color="textSecondary">
              {readOnlyFieldCount} read-only fields
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box width="100%">
            <Typography
              variant="subtitle2"
              sx={{
                pb: 2,
              }}
            >
              Owner Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={selectedCompany.owner.firstName}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={selectedCompany.owner.lastName}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact"
                  name="owner_contact"
                  value={selectedCompany.owner.contact}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="owner_email"
                  value={selectedCompany.owner.email}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Timezone"
                  name="owner_time_zone"
                  value={selectedCompany.owner.timezone}
                  variant="outlined"
                  disabled
                />
              </Grid>
            </Grid>
            <Typography
              variant="subtitle2"
              sx={{
                pt: 2,
                pb: 2,
              }}
            >
              Company Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="company_name"
                  value={selectedCompany.company.name}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Registration No."
                  name="reg_no"
                  value={selectedCompany.company.regNo}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Industry"
                  name="industry"
                  value={
                    industries.filter(
                      (i) =>
                        Number(i.id) === Number(selectedCompany.company.industry)
                    )[0].name
                  }
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={selectedCompany.company.email}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact"
                  name="contact"
                  value={selectedCompany.company.contact}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Line 1"
                  name="line1"
                  value={selectedCompany.company.addressLine1}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Line 2"
                  name="line2"
                  value={selectedCompany.company.addressLine2}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={selectedCompany.company.city}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={selectedCompany.company.state}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={selectedCompany.company.country}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  name="postal_code"
                  value={selectedCompany.company.postcode}
                  variant="outlined"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Article of Assoclation"
                  name="article_assoclation"
                  value="Article of Assoclation"
                  variant="outlined"
                  type="button"
                  onClick={() =>
                    window.open(selectedCompany.articleOfAssoclationUrl, "_blank")
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Director's Identification"
                  name="director_identification"
                  value="Director's Identification"
                  variant="outlined"
                  type="button"
                  onClick={() =>
                    window.open(
                      selectedCompany.directorIdentificationUrl,
                      "_blank"
                    )
                  }
                />
              </Grid>
            </Grid>
            {hasPreSubscription ? (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{
                    pt: 2,
                    pb: 2,
                  }}
                >
                  Plan
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={selectedCompany.preSubscription.name}
                      variant="outlined"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Interval"
                      name="interval"
                      value={selectedCompany.preSubscription.interval}
                      variant="outlined"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Price"
                      name="price"
                      value={selectedCompany.preSubscription.price}
                      variant="outlined"
                      disabled
                    />
                  </Grid>
                </Grid>
              </>
            ) : null}
          </Box>
        </AccordionDetails>
      </Accordion>
      {canEdit && (
        <>
          <Typography variant="subtitle2" sx={sectionSx}>
            Decision
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Approval"
                name="approval"
                onChange={(e) => {
                  handleChange(e);
                  if (!isApprove(e.target.value)) {
                    setActiveStep(0);
                  }
                }}
                onBlur={handleBlur}
                helperText={touched.approval && errors.approval}
                required
                select
                SelectProps={{ native: true }}
                value={values.approval}
                variant="outlined"
                disabled={isSubmitting}
                inputProps={{
                  style: {
                    backgroundColor: "white",
                  },
                }}
              >
                <option value="approve">Approve</option>
                <option value="reject">Reject</option>
                <option value="suspend">Suspend</option>
              </TextField>
            </Grid>
          </Grid>
          <Typography variant="subtitle2" sx={sectionSx}>
            Licence &amp; attribution
          </Typography>
          <Grid container spacing={3}>
            {/* Expire Date Field (Month Picker) startd */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Expire Validity (Months)"
                name="expireDate"
                value={values.expireDate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.expireDate && errors.expireDate)}
                required
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                placeholder="e.g. 12, 24, 36"
                inputProps={{ pattern: "\\d+", maxLength: 3 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sales Email"
                name="salesEmail"
                value={values.salesEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.salesEmail && errors.salesEmail)}
                required
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                placeholder="e.g. sales@example.com"
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                style={{
                  borderRadius: "4px",
                  boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
                  backgroundColor: "white",
                  paddingTop: "10px",
                  paddingBottom: "10px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  marginLeft: "0px",
                  marginRight: "0px",
                  display: "flex",
                  maxWidth: "100%",
                }}
                control={
                  <CustomSwitch
                    value={values.isTestingAccount}
                    onChange={(e) => {
                      setFieldValue("isTestingAccount", e.target.checked);
                    }}
                    name="isTestingAccount"
                  />
                }
                label={
                  <Typography variant="subtitle2">Testing Account?</Typography>
                }
                disableTypography
              />
            </Grid>
            {showExpiryPreview && (
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">
                  Expires on {monthsToExpiryDate(values.expireDate)}
                </Typography>
              </Grid>
            )}
          </Grid>
          {!isApprove(values.approval) && (
            <>
              <Typography variant="subtitle2" sx={sectionSx}>
                Rejection details
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remarks"
                    name="remarks"
                    required
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.remarks}
                    helperText={touched.remarks && errors.remarks}
                    variant="outlined"
                    disabled={isSubmitting}
                  />
                </Grid>
                {values.approval === "reject" ? (
                  <Grid item xs={12}>
                    <TextField
                      variant="outlined"
                      label="Reject Module"
                      name="rejectModules"
                      fullWidth
                      value={values.rejectModules}
                      onChange={async (e) => {
                        await handleChange(e);
                        setFieldTouched("rejectModules", true, true);
                      }}
                      disabled={isSubmitting}
                      helperText={touched.rejectModules && errors.rejectModules}
                      InputLabelProps={{ shrink: true }}
                      InputProps={rejectInputComponentProps}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </>
          )}
          {values.approval !== "reject" &&
          values.approval !== "suspend" &&
          (selectedCompany.registrationType === 2 ||
            selectedCompany.registrationType === 3) ? (
            <>
              <Typography variant="subtitle2" sx={sectionSx}>
                Plan
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Plan"
                    name="plan"
                    onChange={({ target }) => {
                      const data = JSON.parse(target.value);
                      setFieldValue("plan", data);
                      setSelectedPlan(data);
                    }}
                    onBlur={handleBlur}
                    helperText={touched.plan && errors.plan}
                    required
                    select
                    SelectProps={{ native: true }}
                    value={JSON.stringify(values.plan)}
                    variant="outlined"
                    disabled={isSubmitting}
                    inputProps={{
                      style: { textTransform: "capitalize" },
                    }}
                  >
                    {plans.map((plan, i) => (
                      <option
                        value={JSON.stringify(plan)}
                        key={i}
                        style={{ textTransform: "capitalize" }}
                      >
                        {plan.name}
                      </option>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </>
          ) : null}
        </>
      )}
    </>
  );
};

BasicInfoStep.propTypes = {
  industries: PropTypes.array.isRequired,
  selectedCompany: PropTypes.object.isRequired,
  plans: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  setSelectedPlan: PropTypes.func.isRequired,
  setActiveStep: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  canEdit: PropTypes.bool,
  rejectInputComponentProps: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

export default BasicInfoStep;
